import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, X } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { addNotif } from "@/lib/notifications";
import { InvoiceDoc } from "@/components/billing/MyInvoices";

const SEEN = "orbit_inv_seen";
const seen = () => { try { return JSON.parse(localStorage.getItem(SEEN) || "[]"); } catch { return []; } };

export function BillingAlerts() {
  const { staff, t, loaded } = useApp();
  const nav = useNavigate();
  const [open, setOpen] = useState([]);
  const [doc, setDoc] = useState(null);
  const [hidden, setHidden] = useState(false);
  const load = useCallback(async () => {
    try {
      const [inv, n] = await Promise.all([api.get("/billing/invoices"), api.get("/billing/notices")]);
      const issued = inv.data.filter((i) => i.status === "issued");
      setOpen(issued);
      const was = seen();
      const fresh = issued.find((i) => !was.includes(i.id));
      if (fresh) { localStorage.setItem(SEEN, JSON.stringify([...was, ...issued.map((i) => i.id)].slice(-50))); setDoc(fresh); setHidden(false); }
      issued.forEach((i) => addNotif("billing", `${t("inv_invoice")} ${i.number} · ${fmtNum(i.amount)} ${i.currency}`, { key: `inv:${i.id}`, to: "/billing" }));
      n.data.notices.forEach((x) => addNotif("billing", x.text, { key: `bn:${x.id || x.text}`, to: "/billing" }));
      n.data.notices.forEach((x) => toast.info(x.text, { duration: 10000, action: { label: t("billing"), onClick: () => nav("/billing") } }));
    } catch { /* offline or not owner */ }
  }, [nav, t]);
  useEffect(() => {
    if (!loaded || staff?.role !== "owner") return undefined;
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [loaded, staff?.role, load]);
  if (staff?.role !== "owner") return null;
  const onBilling = window.location.pathname.startsWith("/billing");
  return (
    <>
      {open.length > 0 && !hidden && !onBilling && (
        <div className="fixed bottom-20 lg:bottom-4 inset-x-3 lg:start-auto lg:end-4 lg:w-[420px] z-40 rounded-2xl bg-[#0B2A5B] text-white shadow-xl p-3 flex items-center gap-3" data-testid="billing-alert-banner">
          <div className="w-10 h-10 rounded-full bg-[#E67E22] grid place-items-center shrink-0"><FileText className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">{t("inv_alert_title", { n: open.length })}</div>
            <div className="text-xs text-white/80 truncate" dir="ltr">{open.map((i) => `${i.number} · ${fmtNum(i.amount)} ${i.currency}`).join(" · ")}</div>
          </div>
          <button type="button" onClick={() => setDoc(open[0])} className="h-9 px-3 rounded-lg bg-white text-[#0B2A5B] text-xs font-bold" data-testid="billing-alert-view">{t("inv_view")}</button>
          <button type="button" onClick={() => setHidden(true)} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-white/10" aria-label={t("close")} data-testid="billing-alert-close"><X className="w-4 h-4" /></button>
        </div>
      )}
      {doc && <InvoiceDoc inv={doc} onClose={() => setDoc(null)} onClaim={() => { setDoc(null); nav("/billing"); }} />}
    </>
  );
}
